import { useState, useEffect, useRef } from "react";
import { MainLayout } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Camera,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Users,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Star,
  ScanLine,
  Loader2,
  CheckCircle,
  X,
  Filter,
  Calendar,
  UserPlus,
  Settings,
  Send,
  MailCheck,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

// Eventi precaricati
const EVENTI_PRECARICATI = [
  { id: "cosmoprof-bologna-2026", nome: "Cosmoprof Bologna 2026", data: "26-29 Marzo 2026", luogo: "Bologna - Padiglione 29, Stand E20" },
  { id: "beauty-istanbul-2026", nome: "Beauty Istanbul 2026", data: "7-9 Maggio 2026", luogo: "Istanbul - Padiglione 3, Stand 3E 740" },
  { id: "cagliari-2026", nome: "Cagliari 2026", data: "23 Marzo 2026", luogo: "Holiday Inn Cagliari" },
  { id: "tavagnacco-2026", nome: "Tavagnacco (Udine) 2026", data: "13 Aprile 2026", luogo: "Sede CNA" },
  { id: "roma-2026", nome: "Roma 2026", data: "20 Aprile 2026", luogo: "Roma" },
  { id: "cosenza-2026", nome: "Cosenza 2026", data: "4 Maggio 2026", luogo: "Show room Moro I-Tech Beauty" },
  { id: "perugia-2026", nome: "Ponte San Giovanni (Perugia) 2026", data: "25 Maggio 2026", luogo: "CNA Via A. Volta 72" },
  { id: "campania-2026", nome: "Campania 2026", data: "Da definire", luogo: "Campania" },
  { id: "sicilia-2026", nome: "Sicilia 2026", data: "Da definire", luogo: "Sicilia" },
];

const TIPI_CONTATTO = [
  "Centro estetico",
  "Distributore nazionale",
  "Distributore internazionale",
  "Agente",
  "Altro"
];

const LIVELLI_INTERESSE = [
  { value: "basso", label: "Basso", color: "bg-gray-400" },
  { value: "medio", label: "Medio", color: "bg-yellow-500" },
  { value: "alto", label: "Alto", color: "bg-green-500" }
];

const NOTE_RAPIDE = [
  "Interessato all'acquisto",
  "Interessato a diventare distributore",
  "Interessato a diventare agente",
  "Richiede catalogo",
  "Contatto strategico"
];

const PAESI = [
  "Italia", "Turchia", "Spagna", "Francia", "Germania", "Regno Unito", 
  "Portogallo", "Grecia", "Polonia", "Romania", "Svizzera", "Austria",
  "Belgio", "Olanda", "Russia", "USA", "Emirati Arabi", "Arabia Saudita", "Altro"
];

export default function AcquisizioneContattiFieraPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  
  const [contatti, setContatti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingContatto, setEditingContatto] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null); // ID del contatto a cui stiamo inviando email
  
  // Gestione operatori fiera (solo admin)
  const [showOperatoriDialog, setShowOperatoriDialog] = useState(false);
  const [operatori, setOperatori] = useState([]);
  const [newOperatore, setNewOperatore] = useState({ name: "", email: "", password: "", phone: "" });
  
  // Filtri
  const [filtroEvento, setFiltroEvento] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroPaese, setFiltroPaese] = useState("");
  const [filtroInteresse, setFiltroInteresse] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form data
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    azienda: "",
    tipo_contatto: "",
    telefono: "",
    email: "",
    citta: "",
    paese: "Italia",
    regione_interesse: "",
    prodotti_interesse: "",
    livello_interesse: "medio",
    evento: "",
    note: "",
    sito_web: ""
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchContatti();
    if (isAdmin) {
      fetchOperatori();
    }
  }, [filtroEvento, filtroTipo, filtroPaese, filtroInteresse]);

  const fetchOperatori = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/operatori-fiera`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOperatori(data);
      }
    } catch (error) {
      console.error("Errore caricamento operatori:", error);
    }
  };

  const createOperatore = async () => {
    if (!newOperatore.name || !newOperatore.email || !newOperatore.password) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/operatori-fiera`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newOperatore)
      });
      if (response.ok) {
        toast.success("Operatore creato con successo!");
        setNewOperatore({ name: "", email: "", password: "", phone: "" });
        fetchOperatori();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore nella creazione dell'operatore");
    }
  };

  const deleteOperatore = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/operatori-fiera/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Operatore eliminato");
        fetchOperatori();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const fetchContatti = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filtroEvento) params.append("evento", filtroEvento);
      if (filtroTipo) params.append("tipo_contatto", filtroTipo);
      if (filtroPaese) params.append("paese", filtroPaese);
      if (filtroInteresse) params.append("livello_interesse", filtroInteresse);
      
      const response = await fetch(`${API_URL}/api/contatti-fiera?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContatti(data);
      }
    } catch (error) {
      console.error("Errore caricamento contatti:", error);
      toast.error("Errore nel caricamento dei contatti");
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    setShowCamera(true);
    setCapturedImage(null);
    setOcrResult(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Errore accesso fotocamera:", error);
      toast.error("Impossibile accedere alla fotocamera. Usa 'Carica Immagine' per selezionare un file.");
      setShowCamera(false);
      // Apri automaticamente il selettore file se la fotocamera non è disponibile
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL("image/jpeg", 0.8);
      setCapturedImage(imageData);
      stopCamera();
      processOCR(imageData);
    }
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Funzione per comprimere le immagini prima dell'invio OCR
  const compressImage = (dataUrl, maxWidth = 1000, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Ridimensiona se necessario
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Converti in JPEG con qualità specificata
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        console.log(`Immagine compressa: ${Math.round(compressedDataUrl.length / 1024)} KB`);
        resolve(compressedDataUrl);
      };
      img.src = dataUrl;
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Mostra subito lo stato di scansione
      setShowCamera(true);
      setScanning(true);
      
      try {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const imageData = reader.result;
          
          // Comprimi l'immagine se è troppo grande
          let processedImage = imageData;
          if (file.size > 500000) { // > 500KB
            console.log(`Immagine originale: ${Math.round(file.size / 1024)} KB, compressione in corso...`);
            processedImage = await compressImage(imageData, 1000, 0.85);
          }
          
          setCapturedImage(processedImage);
          processOCR(processedImage);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Errore lettura file:", error);
        toast.error("Errore nella lettura del file");
        setScanning(false);
      }
    }
  };

  const processOCR = async (imageData) => {
    setScanning(true);
    try {
      const token = localStorage.getItem("token");
      const base64Data = imageData.split(",")[1];
      
      // Log dimensione per debug
      console.log(`Invio OCR: ${Math.round(base64Data.length / 1024)} KB`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout
      
      const response = await fetch(`${API_URL}/api/contatti-fiera/ocr`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ image_base64: base64Data }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        setOcrResult(data);
        setFormData(prev => ({
          ...prev,
          nome: data.nome || "",
          cognome: data.cognome || "",
          azienda: data.azienda || "",
          telefono: data.telefono || "",
          email: data.email || "",
          citta: data.citta || "",
          paese: data.paese || "Italia",
          sito_web: data.sito_web || ""
        }));
        toast.success("Biglietto da visita analizzato!");
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "Errore nell'analisi OCR. Prova con un'immagine più nitida.");
      }
    } catch (error) {
      console.error("Errore OCR:", error);
      if (error.name === 'AbortError') {
        toast.error("Timeout: l'analisi ha impiegato troppo tempo. Prova con un'immagine più piccola.");
      } else {
        toast.error("Errore durante l'analisi del biglietto. Verifica la connessione.");
      }
    } finally {
      setScanning(false);
    }
  };

  const confirmOCRAndShowForm = () => {
    setShowCamera(false);
    setShowForm(true);
  };

  // Salva direttamente dopo OCR senza passare dal form
  const saveOCRDirectly = async () => {
    if (!ocrResult) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      
      // Prepara i dati con defaults
      const dataToSave = {
        nome: ocrResult.nome || "",
        cognome: ocrResult.cognome || "",
        azienda: ocrResult.azienda || "",
        telefono: ocrResult.telefono || "",
        email: ocrResult.email || "",
        citta: ocrResult.citta || "",
        paese: ocrResult.paese || "Italia",
        sito_web: ocrResult.sito_web || "",
        tipo_contatto: "Centro Estetico", // Default
        livello_interesse: "medio",
        evento: filtroEvento || "",
        note: "Acquisito tramite scansione biglietto da visita"
      };
      
      const response = await fetch(`${API_URL}/api/contatti-fiera`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(dataToSave)
      });
      
      if (response.ok) {
        toast.success("Contatto salvato con successo!");
        setShowCamera(false);
        setCapturedImage(null);
        setOcrResult(null);
        fetchContatti();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      console.error("Errore salvataggio diretto:", error);
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      cognome: "",
      azienda: "",
      tipo_contatto: "",
      telefono: "",
      email: "",
      citta: "",
      paese: "Italia",
      regione_interesse: "",
      prodotti_interesse: "",
      livello_interesse: "medio",
      evento: "",
      note: "",
      sito_web: ""
    });
    setEditingContatto(null);
    setCapturedImage(null);
    setOcrResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem("token");
      const url = editingContatto 
        ? `${API_URL}/api/contatti-fiera/${editingContatto.contatto_id}`
        : `${API_URL}/api/contatti-fiera`;
      
      const response = await fetch(url, {
        method: editingContatto ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingContatto ? "Contatto aggiornato!" : "Contatto salvato!");
        setShowForm(false);
        resetForm();
        fetchContatti();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      console.error("Errore salvataggio:", error);
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (contatto) => {
    setFormData({
      nome: contatto.nome || "",
      cognome: contatto.cognome || "",
      azienda: contatto.azienda || "",
      tipo_contatto: contatto.tipo_contatto || "",
      telefono: contatto.telefono || "",
      email: contatto.email || "",
      citta: contatto.citta || "",
      paese: contatto.paese || "Italia",
      regione_interesse: contatto.regione_interesse || "",
      prodotti_interesse: contatto.prodotti_interesse || "",
      livello_interesse: contatto.livello_interesse || "medio",
      evento: contatto.evento || "",
      note: contatto.note || "",
      sito_web: contatto.sito_web || ""
    });
    setEditingContatto(contatto);
    setShowForm(true);
  };

  const handleDelete = async (contattoId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/contatti-fiera/${contattoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Contatto eliminato");
        setShowDeleteConfirm(null);
        fetchContatti();
      } else {
        toast.error("Errore durante l'eliminazione");
      }
    } catch (error) {
      console.error("Errore eliminazione:", error);
      toast.error("Errore durante l'eliminazione");
    }
  };

  // Invia email di ringraziamento
  const sendThankYouEmail = async (contatto) => {
    if (!contatto.email) {
      toast.error("Questo contatto non ha un indirizzo email");
      return;
    }
    
    setSendingEmail(contatto.contatto_id);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/contatti-fiera/${contatto.contatto_id}/send-thank-you`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ contatto_id: contatto.contatto_id })
      });
      
      if (response.ok) {
        toast.success(`Email inviata a ${contatto.email}!`);
        // Aggiorna la lista contatti per riflettere l'invio
        fetchContatti();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio email");
      }
    } catch (error) {
      console.error("Errore invio email:", error);
      toast.error("Errore durante l'invio dell'email");
    } finally {
      setSendingEmail(null);
    }
  };

  // Invia email a tutti i contatti di un evento (solo admin)
  const sendBulkThankYouEmails = async () => {
    if (!filtroEvento) {
      toast.error("Seleziona prima un evento per inviare email in massa");
      return;
    }
    
    const conferma = window.confirm(`Vuoi inviare email di ringraziamento a TUTTI i contatti dell'evento "${EVENTI_PRECARICATI.find(e => e.id === filtroEvento)?.nome || filtroEvento}" che non l'hanno ancora ricevuta?`);
    if (!conferma) return;
    
    setSendingEmail("bulk");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/contatti-fiera/send-bulk-thank-you?evento=${filtroEvento}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Email inviate: ${data.sent}, Fallite: ${data.failed}`);
        fetchContatti();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio email");
      }
    } catch (error) {
      console.error("Errore invio bulk:", error);
      toast.error("Errore durante l'invio delle email");
    } finally {
      setSendingEmail(null);
    }
  };

  const addQuickNote = (nota) => {
    setFormData(prev => ({
      ...prev,
      note: prev.note ? `${prev.note}\n${nota}` : nota
    }));
  };

  const exportToCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filtroEvento) params.append("evento", filtroEvento);
      if (filtroTipo) params.append("tipo_contatto", filtroTipo);
      if (filtroPaese) params.append("paese", filtroPaese);
      if (filtroInteresse) params.append("livello_interesse", filtroInteresse);
      
      const response = await fetch(`${API_URL}/api/contatti-fiera/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `contatti_fiera_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Export completato!");
      }
    } catch (error) {
      console.error("Errore export:", error);
      toast.error("Errore durante l'export");
    }
  };

  const filteredContatti = contatti.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.nome?.toLowerCase().includes(query) ||
      c.cognome?.toLowerCase().includes(query) ||
      c.azienda?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.citta?.toLowerCase().includes(query)
    );
  });

  const getInterestBadge = (livello) => {
    const interest = LIVELLI_INTERESSE.find(l => l.value === livello);
    return interest ? (
      <Badge className={`${interest.color} text-white`}>
        {interest.label}
      </Badge>
    ) : null;
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              Acquisizione Contatti Fiera
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestisci i contatti acquisiti durante fiere ed eventi
            </p>
          </div>
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={() => setShowOperatoriDialog(true)}
              className="flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline">Gestisci Operatori</span>
            </Button>
          )}
        </div>

        {/* Azioni principali - Mobile First */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <Button 
            onClick={startCamera}
            className="h-16 text-base font-semibold flex flex-col gap-1"
            data-testid="scan-card-btn"
          >
            <Camera className="w-6 h-6" />
            <span className="hidden md:inline">Usa Fotocamera</span>
            <span className="md:hidden">Scansiona</span>
          </Button>
          <Button 
            onClick={handleFileSelect}
            variant="secondary"
            className="h-16 text-base font-semibold flex flex-col gap-1"
            data-testid="upload-image-btn"
          >
            <ScanLine className="w-6 h-6" />
            Carica Immagine
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowForm(true); }}
            variant="outline"
            className="h-16 text-base font-semibold flex flex-col gap-1 col-span-2 md:col-span-1"
            data-testid="manual-entry-btn"
          >
            <Plus className="w-6 h-6" />
            Inserimento Manuale
          </Button>
        </div>

        {/* Input file nascosto per upload foto */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Filtri */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtri
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select value={filtroEvento} onValueChange={(v) => setFiltroEvento(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-evento">
                  <SelectValue placeholder="Evento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli eventi</SelectItem>
                  {EVENTI_PRECARICATI.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filtroTipo} onValueChange={(v) => setFiltroTipo(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-tipo">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  {TIPI_CONTATTO.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filtroPaese} onValueChange={(v) => setFiltroPaese(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-paese">
                  <SelectValue placeholder="Paese" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i paesi</SelectItem>
                  {PAESI.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filtroInteresse} onValueChange={(v) => setFiltroInteresse(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-interesse">
                  <SelectValue placeholder="Interesse" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {LIVELLI_INTERESSE.map(l => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 mt-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome, azienda, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={exportToCSV} variant="outline" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                {isAdmin && filtroEvento && (
                  <Button 
                    onClick={sendBulkThankYouEmails}
                    variant="secondary"
                    disabled={sendingEmail === "bulk"}
                    data-testid="bulk-email-btn"
                    className="bg-purple-100 hover:bg-purple-200 text-purple-700"
                  >
                    {sendingEmail === "bulk" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Invia Email a Tutti
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-primary">{contatti.length}</div>
              <div className="text-sm text-muted-foreground">Totale</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-green-600">
                {contatti.filter(c => c.livello_interesse === "alto").length}
              </div>
              <div className="text-sm text-muted-foreground">Alto interesse</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-blue-600">
                {[...new Set(contatti.map(c => c.paese))].length}
              </div>
              <div className="text-sm text-muted-foreground">Paesi</div>
            </CardContent>
          </Card>
        </div>

        {/* Lista contatti */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredContatti.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {contatti.length === 0 
                  ? "Nessun contatto acquisito. Inizia scansionando un biglietto da visita!"
                  : "Nessun contatto trovato con i filtri selezionati"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredContatti.map((contatto) => (
              <Card key={contatto.contatto_id} className="overflow-hidden" data-testid={`contatto-${contatto.contatto_id}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">
                          {contatto.nome} {contatto.cognome}
                        </h3>
                        {getInterestBadge(contatto.livello_interesse)}
                      </div>
                      
                      {contatto.azienda && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <Building2 className="w-4 h-4" />
                          {contatto.azienda}
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {contatto.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {contatto.email}
                          </span>
                        )}
                        {contatto.telefono && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {contatto.telefono}
                          </span>
                        )}
                        {contatto.citta && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {contatto.citta}, {contatto.paese}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mt-2">
                        {contatto.tipo_contatto && (
                          <Badge variant="outline">{contatto.tipo_contatto}</Badge>
                        )}
                        {contatto.evento && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {EVENTI_PRECARICATI.find(e => e.id === contatto.evento)?.nome || contatto.evento}
                          </Badge>
                        )}
                        {contatto.email_ringraziamento_inviata && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1">
                            <MailCheck className="w-3 h-3" />
                            Email inviata
                          </Badge>
                        )}
                      </div>

                      {contatto.note && (
                        <p className="text-sm text-muted-foreground mt-2 italic">
                          "{contatto.note}"
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {/* Bottone Invia Email Ringraziamento */}
                      {contatto.email && !contatto.email_ringraziamento_inviata && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          onClick={() => sendThankYouEmail(contatto)}
                          disabled={sendingEmail === contatto.contatto_id}
                          data-testid={`email-${contatto.contatto_id}`}
                        >
                          {sendingEmail === contatto.contatto_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Ringrazia
                            </>
                          )}
                        </Button>
                      )}
                      <div className="flex gap-2">
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => handleEdit(contatto)}
                          data-testid={`edit-${contatto.contatto_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => setShowDeleteConfirm(contatto)}
                          data-testid={`delete-${contatto.contatto_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog Fotocamera / OCR */}
        <Dialog open={showCamera} onOpenChange={(open) => { if (!open) stopCamera(); setShowCamera(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ScanLine className="w-5 h-5" />
                Scansiona Biglietto da Visita
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {!capturedImage ? (
                <>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline
                    className="w-full rounded-lg bg-black aspect-video"
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  <div className="flex gap-3">
                    <Button onClick={captureImage} className="flex-1 h-14" data-testid="capture-btn">
                      <Camera className="w-5 h-5 mr-2" />
                      Scatta Foto
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      className="h-14"
                    >
                      Carica da Galleria
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <img 
                    src={capturedImage} 
                    alt="Biglietto catturato" 
                    className="w-full rounded-lg"
                  />
                  
                  {scanning ? (
                    <div className="flex items-center justify-center py-4 gap-3">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <span>Analisi in corso...</span>
                    </div>
                  ) : ocrResult ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Dati estratti con successo!</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {ocrResult.nome && <div><strong>Nome:</strong> {ocrResult.nome}</div>}
                        {ocrResult.cognome && <div><strong>Cognome:</strong> {ocrResult.cognome}</div>}
                        {ocrResult.azienda && <div className="col-span-2"><strong>Azienda:</strong> {ocrResult.azienda}</div>}
                        {ocrResult.email && <div className="col-span-2"><strong>Email:</strong> {ocrResult.email}</div>}
                        {ocrResult.telefono && <div><strong>Tel:</strong> {ocrResult.telefono}</div>}
                        {ocrResult.citta && <div><strong>Città:</strong> {ocrResult.citta}</div>}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button 
                          onClick={saveOCRDirectly} 
                          className="w-full bg-green-600 hover:bg-green-700" 
                          disabled={saving}
                          data-testid="save-ocr-direct-btn"
                        >
                          {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Salva Subito
                        </Button>
                        <div className="flex gap-2">
                          <Button onClick={confirmOCRAndShowForm} variant="outline" className="flex-1" data-testid="confirm-ocr-btn">
                            <Edit className="w-4 h-4 mr-2" />
                            Modifica prima
                          </Button>
                          <Button variant="ghost" onClick={() => { setCapturedImage(null); setOcrResult(null); startCamera(); }}>
                            Riprova
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Form */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingContatto ? "Modifica Contatto" : "Nuovo Contatto"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Dati personali */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Nome *</label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    required
                    data-testid="input-nome"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Cognome *</label>
                  <Input
                    value={formData.cognome}
                    onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                    required
                    data-testid="input-cognome"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Azienda / Centro Estetico</label>
                <Input
                  value={formData.azienda}
                  onChange={(e) => setFormData({...formData, azienda: e.target.value})}
                  data-testid="input-azienda"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Tipo Contatto *</label>
                  <Select 
                    value={formData.tipo_contatto} 
                    onValueChange={(v) => setFormData({...formData, tipo_contatto: v})}
                  >
                    <SelectTrigger data-testid="select-tipo">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPI_CONTATTO.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Evento *</label>
                  <Select 
                    value={formData.evento} 
                    onValueChange={(v) => setFormData({...formData, evento: v})}
                  >
                    <SelectTrigger data-testid="select-evento">
                      <SelectValue placeholder="Seleziona evento" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENTI_PRECARICATI.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Telefono</label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    type="tel"
                    data-testid="input-telefono"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    type="email"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Città</label>
                  <Input
                    value={formData.citta}
                    onChange={(e) => setFormData({...formData, citta: e.target.value})}
                    data-testid="input-citta"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Paese</label>
                  <Select 
                    value={formData.paese} 
                    onValueChange={(v) => setFormData({...formData, paese: v})}
                  >
                    <SelectTrigger data-testid="select-paese">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAESI.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Sito Web</label>
                <Input
                  value={formData.sito_web}
                  onChange={(e) => setFormData({...formData, sito_web: e.target.value})}
                  placeholder="https://..."
                  data-testid="input-sito"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Regione di Interesse</label>
                  <Input
                    value={formData.regione_interesse}
                    onChange={(e) => setFormData({...formData, regione_interesse: e.target.value})}
                    placeholder="Es: Lombardia, Nord Italia"
                    data-testid="input-regione"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Prodotti di Interesse</label>
                  <Input
                    value={formData.prodotti_interesse}
                    onChange={(e) => setFormData({...formData, prodotti_interesse: e.target.value})}
                    placeholder="Es: Linea viso, Trattamenti"
                    data-testid="input-prodotti"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Livello di Interesse</label>
                <div className="flex gap-3 mt-2">
                  {LIVELLI_INTERESSE.map(l => (
                    <Button
                      key={l.value}
                      type="button"
                      variant={formData.livello_interesse === l.value ? "default" : "outline"}
                      className={formData.livello_interesse === l.value ? l.color : ""}
                      onClick={() => setFormData({...formData, livello_interesse: l.value})}
                      data-testid={`interesse-${l.value}`}
                    >
                      <Star className="w-4 h-4 mr-1" />
                      {l.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Note Commerciali</label>
                <Textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  rows={3}
                  placeholder="Inserisci note commerciali..."
                  data-testid="input-note"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {NOTE_RAPIDE.map(nota => (
                    <Button
                      key={nota}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addQuickNote(nota)}
                    >
                      + {nota}
                    </Button>
                  ))}
                </div>
              </div>

              <DialogFooter className="gap-3">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Annulla
                </Button>
                <Button type="submit" disabled={saving} data-testid="submit-btn">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {editingContatto ? "Aggiorna" : "Salva Contatto"}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Conferma Eliminazione */}
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Conferma Eliminazione</DialogTitle>
            </DialogHeader>
            <p>
              Sei sicuro di voler eliminare il contatto <strong>{showDeleteConfirm?.nome} {showDeleteConfirm?.cognome}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Annulla
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDelete(showDeleteConfirm?.contatto_id)}
              >
                Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Gestione Operatori Fiera (solo Admin) */}
        {isAdmin && (
          <Dialog open={showOperatoriDialog} onOpenChange={setShowOperatoriDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Gestione Operatori Fiera
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Form nuovo operatore */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Aggiungi Nuovo Operatore</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Nome completo *"
                        value={newOperatore.name}
                        onChange={(e) => setNewOperatore({...newOperatore, name: e.target.value})}
                      />
                      <Input
                        placeholder="Email *"
                        type="email"
                        value={newOperatore.email}
                        onChange={(e) => setNewOperatore({...newOperatore, email: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Password *"
                        type="password"
                        value={newOperatore.password}
                        onChange={(e) => setNewOperatore({...newOperatore, password: e.target.value})}
                      />
                      <Input
                        placeholder="Telefono"
                        value={newOperatore.phone}
                        onChange={(e) => setNewOperatore({...newOperatore, phone: e.target.value})}
                      />
                    </div>
                    <Button onClick={createOperatore} className="w-full">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Crea Operatore
                    </Button>
                  </CardContent>
                </Card>

                {/* Lista operatori esistenti */}
                <div>
                  <h3 className="font-medium mb-3">Operatori Attivi ({operatori.length})</h3>
                  {operatori.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nessun operatore fiera creato
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {operatori.map((op) => (
                        <div key={op.user_id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                          <div>
                            <p className="font-medium">{op.name}</p>
                            <p className="text-sm text-muted-foreground">{op.email}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => deleteOperatore(op.user_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-amber-800">Nota:</p>
                  <p className="text-amber-700">
                    Gli operatori fiera possono vedere solo la pagina "Acquisizione Contatti" e i propri contatti.
                    Non hanno accesso ad altre sezioni dell'applicazione.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </MainLayout>
  );
}
